/** @jsxImportSource @emotion/react */
import { useState } from 'react';
import { css } from '@emotion/react';
import ModalView, { ModalContent } from '../layout/modal/ModalView';
import ModalTitleHeader from '../layout/modal/ModalTitleHeader';
import { Column, Row } from '../layout/flexbox';
import * as Buttons from '../forms/elements/buttons';
import { Input } from '../forms/elements/Input';
import LabelGroup from '../forms/elements/LabelGroup';
import { COLUMN_SPACING, ROW_SPACING } from '../forms/styles';
import { getResource } from '../language/resources/getResource';
import { TrainingDocument } from './trainingDocument';


const isDocxFile = (file: File) => file.name.toLowerCase().endsWith('.docx');

const fieldSpacing = css`
  margin-bottom: 15px;
`;


/**
 * TrainingDocumentFormModal: the admin form for uploading a new training document or replacing an existing one.
 * Pass documentToReplace for replace mode — the title prefills and the file becomes optional (omitting it updates the
 * metadata only). Upload mode requires a .docx before submit enables.
 */
export default function TrainingDocumentFormModal(props: {
  resources: Record<string, any>;
  documentToReplace?: TrainingDocument;
  onSubmit: (title: string, sortOrder: number | undefined, file: File | undefined) => void;
  onDismiss: () => void;
}) {
  const { resources, documentToReplace } = props;
  const [file, setFile] = useState<File | undefined>(undefined);
  const [title, setTitle] = useState(documentToReplace?.title ?? '');
  const [sortOrder, setSortOrder] = useState(documentToReplace?.sortOrder != null ? String(documentToReplace.sortOrder) : '');

  const isTitleValid = title.trim().length > 0 && title.trim().length <= 200;
  const isFileValid = documentToReplace
                      ? !file || isDocxFile(file)
                      : !!file && isDocxFile(file);
  const canSubmit = isTitleValid && isFileValid;

  const submit = () => props.onSubmit(
    title.trim(),
    sortOrder.trim() === '' ? undefined : Number(sortOrder),
    file,
  );

  return (
    <ModalView onDismiss={props.onDismiss}>
      <ModalTitleHeader
        resources={resources}
        heading={getResource(resources, documentToReplace ? 'HELP_TRAINING_FORM_TITLE_REPLACE' : 'HELP_TRAINING_FORM_TITLE_UPLOAD')}
        onDismiss={props.onDismiss}
      />
      <ModalContent>
        <Column flexGrow={1} rootCss={COLUMN_SPACING}>
          <LabelGroup
            label={getResource(resources, 'HELP_TRAINING_FORM_FILE_LABEL')}
            showRequired={!documentToReplace}
            rootCss={fieldSpacing}
          >
            <input
              type="file"
              accept=".docx"
              onChange={event => setFile(event.target.files?.[0] ?? undefined)}
            />
          </LabelGroup>
          <LabelGroup
            label={getResource(resources, 'HELP_TRAINING_FORM_DOC_TITLE_LABEL')}
            showRequired={true}
            rootCss={fieldSpacing}
          >
            <Input
              type="text"
              maxLength={200}
              value={title}
              onChange={event => setTitle(event.target.value)}
            />
          </LabelGroup>
          <LabelGroup
            label={getResource(resources, 'HELP_TRAINING_FORM_SORT_ORDER_LABEL')}
            rootCss={fieldSpacing}
          >
            <Input
              type="number"
              value={sortOrder}
              onChange={event => setSortOrder(event.target.value)}
            />
          </LabelGroup>
          <Row alignItems="baseline" justifyContent="flex-end" rootCss={ROW_SPACING}>
            <Buttons.BigSecondary type="button" onClick={props.onDismiss}>
              {getResource(resources, 'HELP_TRAINING_FORM_CANCEL')}
            </Buttons.BigSecondary>
            <Buttons.BigNeutral type="button" disabled={!canSubmit} onClick={submit}>
              {getResource(resources, 'HELP_TRAINING_FORM_SUBMIT')}
            </Buttons.BigNeutral>
          </Row>
        </Column>
      </ModalContent>
    </ModalView>
  );
}
